import { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import SocialSection from '../components/SocialSection';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID_ABOUT = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

const AboutPage = () => {
  const { t, i18n } = useTranslation();
  return (
    <div className="min-h-screen bg-black">
      {/* Background Video */}
      <div className="fixed top-0 left-0 w-full h-full z-0">
        <video
          className="w-full h-full object-cover"
          src="/vidoes/70 - headersite 2_2.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-60"></div>
      </div>
      
      {/* Page Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-24 text-white" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
        
        {/* Intro Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-4">
            {t('about.title')}
          </h1>
          <div className="max-w-4xl mx-auto text-lg text-gray-200 space-y-4">
            <p>{t('about.intro1')}</p>
            <p>{t('about.intro2')}</p>
            <p>{t('about.intro3')}</p>
          </div>
        </div>

        {/* Features Section */}
        <div className="max-w-4xl mx-auto mb-16">
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <li className="bg-gray-800 bg-opacity-70 p-4 rounded-lg flex flex-col items-center justify-center">{t('about.feature1')}</li>
                <li className="bg-gray-800 bg-opacity-70 p-4 rounded-lg flex flex-col items-center justify-center">{t('about.feature2')}</li>
                <li className="bg-gray-800 bg-opacity-70 p-4 rounded-lg flex flex-col items-center justify-center">{t('about.feature3')}</li>
                <li className="bg-gray-800 bg-opacity-70 p-4 rounded-lg flex flex-col items-center justify-center">{t('about.feature4')}</li>
            </ul>
        </div>
        <p className="max-w-4xl mx-auto text-lg text-center text-gray-200 mb-16">
            {t('about.mission')}
        </p>


        {/* Founders Section */}
        <div className="bg-gray-900 bg-opacity-70 rounded-lg shadow-xl overflow-hidden mb-16 backdrop-blur-sm p-8 lg:p-12">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold mb-4">{t('about.ownerName')}</h2>
              <p className="text-gray-300 text-base leading-relaxed mb-6">
                {t('about.ownerBio')}
              </p>
              
              <h2 className="text-3xl font-bold mb-4">{t('about.coFounderName')}</h2>
              <p className="text-gray-300 text-base leading-relaxed">
                {t('about.coFounderBio')}
              </p>
            </div>
            <div className="md:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <img src="/pictures/26-DSC00237.jpg" alt="Ron Berko 1" className="w-full h-full object-cover"/>
              <img src="/pictures/27-DSC00213.jpg" alt="Ron Berko 2" className="w-full h-full object-cover"/>
              <img src="/pictures/28-DSC00157.jpg" alt="Ron Berko 3" className="col-span-1 sm:col-span-2 w-full h-full object-cover"/>
            </div>
          </div>
        </div>

        {/* Test Drive Section */}
        <TestDriveSection />

        <div className="h-40 bg-transparent"></div>
        
        {/* Social Section - minimalized version */}
        <SocialSection />
      </div>
    </div>
  );
};

const TestDriveSection = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSent, setIsSent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phoneNumber || ''
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!EMAILJS_SERVICE_ID || EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID') {
      setError(t('about.emailjsNotConfigured'));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID_ABOUT,
        formData,
        EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setIsSent(true);
        setIsSubmitting(false);
      }, (err) => {
        console.error('FAILED...', err);
        setError(t('about.sendFailed'));
        setIsSubmitting(false);
      });
  };

  return (
    <section className="bg-gray-900 bg-opacity-70 rounded-lg shadow-xl overflow-hidden backdrop-blur-sm p-8 lg:p-12">
        <div className="text-center mb-8">
            <h2 className="text-4xl font-bold">{t('about.testDriveTitle')}</h2>
            <p className="text-lg text-gray-300 mt-2">{t('about.testDriveSubtitle')}</p>
        </div>
        <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2">
                {isSent ? (
                    <div className="text-center bg-green-500 bg-opacity-80 p-8 rounded-lg">
                        <h3 className="text-2xl font-bold">{t('about.thankYou')}</h3>
                        <p className="mt-2">{t('about.messageSent')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder={t('about.formName')}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={t('about.formEmail')}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder={t('about.formPhone')}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            dir="ltr"
                            required
                        />
                        <button
                            type="submit"
                            className="w-full bg-blue-600 font-bold py-3 px-4 rounded-lg hover:bg-blue-700 transition-transform disabled:bg-gray-500"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? t('about.sending') : t('about.send')}
                        </button>
                        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
                    </form>
                )}
            </div>
            <div className="md:w-1/2">
                <img src="/pictures/29-DSC00146.jpg" alt="Test Drive" className="w-full h-full object-cover rounded-lg shadow-lg"/>
            </div>
        </div>
    </section>
  );
}


export default AboutPage;
